import { Panel, Dropdown, IconButton } from 'rsuite';
import { FaWhatsapp, FaEllipsisV, FaHeading, FaCode, FaRegDotCircle, FaLock, FaEyeSlash, FaUndo } from 'react-icons/fa';
import { useCustomTheme } from '../../../hooks/useCustomTheme';
import TemplateBubble from './TemplateBubble';
import { STATUS, CATEGORY_LABEL, EDITABLE_STATUS, numberedVars, PROTECTED_TEMPLATE_NAME } from './templateConstants';

export default function TemplateCard({ template, showPreview = true, onEdit, onDelete, onRestore }) {
  const { theme } = useCustomTheme();
  const status = STATUS[template.status] || { label: template.status || '—', color: 'blue' };
  const vars = numberedVars(template.bodyText);
  const buttons = Array.isArray(template.buttons) ? template.buttons : [];
  const canEdit = EDITABLE_STATUS.includes(template.status);
  const isProtected = template.name === PROTECTED_TEMPLATE_NAME;
  const isHidden = Boolean(template.hidden);
  const isPending = ['PENDING', 'IN_APPEAL'].includes(template.status) && !isHidden;
  const getExample = (n) => (template.exampleValues || [])[vars.indexOf(n)];
  const showQuality = template.qualityScore && template.qualityScore !== 'UNKNOWN';

  return (
    <Panel bordered style={card(theme, isProtected, isHidden)} bodyFill>
      <div style={head}>
        <div style={icon(theme)}><FaWhatsapp size={16} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={nameRow}>
            <span style={nameStyle(theme)}>{template.name}</span>
            {isProtected && <span style={protectedBadge(theme)}><FaLock size={9} /> Padrão</span>}
            {isHidden && <span style={hiddenBadge(theme)}><FaEyeSlash size={9} /> Oculto</span>}
          </div>
          <div style={meta(theme)}>
            {CATEGORY_LABEL[template.category] || template.category || '—'} · {template.language || 'pt_BR'}
          </div>
        </div>
        <Dropdown placement="bottomEnd" renderToggle={(props) => <IconButton {...props} appearance="subtle" size="sm" icon={<FaEllipsisV />} />}>
          {isHidden ? (
            <Dropdown.Item onSelect={onRestore} icon={<FaUndo />}>Restaurar</Dropdown.Item>
          ) : (
            <>
              <Dropdown.Item disabled={!canEdit} onSelect={onEdit}>Editar</Dropdown.Item>
              {isProtected ? (
                <Dropdown.Item disabled icon={<FaLock />}>Protegido</Dropdown.Item>
              ) : (
                <Dropdown.Item onSelect={onDelete} style={{ color: '#f5222d' }}>Excluir</Dropdown.Item>
              )}
            </>
          )}
        </Dropdown>
      </div>

      <div style={statusRow}>
        <span style={dot(status.color)} />
        <span style={statusLabel(theme)}>{status.label}</span>
        {showQuality && <span style={qualityChip(theme)}>Qualidade {template.qualityScore}</span>}
      </div>

      {showPreview && (
        <div style={{ padding: '0 12px' }}>
          <TemplateBubble
            headerType={template.headerType}
            headerContent={template.headerContent}
            bodyText={template.bodyText}
            footerText={template.footerText}
            buttons={buttons}
            getExample={getExample}
          />
        </div>
      )}

      <div style={chips}>
        {template.headerType && template.headerType !== 'NONE' && <span style={chip(theme)}><FaHeading size={10} /> Cabeçalho: {template.headerType}</span>}
        {vars.length > 0 && <span style={chip(theme)}><FaCode size={10} /> {vars.length} variáveis</span>}
        {buttons.length > 0 && <span style={chip(theme)}><FaRegDotCircle size={10} /> {buttons.length} botões</span>}
      </div>

      {isPending && (
        <div style={pendingNotice(theme, isProtected)}>
          ⏳ Aguardando aprovação da Meta — costuma levar de alguns minutos a algumas horas (em geral até 24h).
          {isProtected ? ' As notificações de pedido começam a funcionar assim que ele for aprovado.' : ' Ele poderá ser enviado após a aprovação.'}
        </div>
      )}

      {template.status === 'REJECTED' && template.rejectedReason && (
        <div style={rejectReason}>Motivo: {template.rejectedReason}</div>
      )}
    </Panel>
  );
}

const card = (theme, isProtected, isHidden) => ({
  borderRadius: 14,
  overflow: 'hidden',
  background: theme.colors.backgroundSecondary,
  border: `1px solid ${isProtected ? theme.colors.primary : 'rgba(0,0,0,0.06)'}`,
  boxShadow: isProtected ? `0 6px 20px ${theme.colors.primary}22` : '0 6px 20px rgba(0,0,0,0.06)',
  paddingBottom: 12,
  opacity: isHidden ? 0.72 : 1,
});
const head = { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 14px 8px' };
const nameRow = { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 };
const nameStyle = (theme) => ({ fontSize: 15, fontWeight: 600, color: theme.colors.text, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' });
const protectedBadge = (theme) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, padding: '2px 7px', borderRadius: 20, color: theme.colors.primary, background: `${theme.colors.primary}18` });
const hiddenBadge = (theme) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, padding: '2px 7px', borderRadius: 20, color: theme.colors.text, background: 'rgba(0,0,0,0.09)', opacity: 0.75 });
const icon = (theme) => ({ width: 34, height: 34, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', flexShrink: 0, background: theme.colors.primary });
const meta = (theme) => ({ fontSize: 12, color: theme.colors.text, opacity: 0.6 });
const statusRow = { display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 10px' };
const dot = (color) => ({ width: 9, height: 9, borderRadius: '50%', background: color === 'green' ? '#21ba45' : color === 'red' ? '#f5222d' : '#faad14', boxShadow: `0 0 0 3px ${color === 'green' ? 'rgba(33,186,69,0.15)' : color === 'red' ? 'rgba(245,34,45,0.15)' : 'rgba(250,173,20,0.15)'}` });
const statusLabel = (theme) => ({ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: theme.colors.text, opacity: 0.8 });
const qualityChip = (theme) => ({ marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 20, background: theme.colors.background, color: theme.colors.text, opacity: 0.7 });
const chips = { display: 'flex', flexWrap: 'wrap', gap: 6, padding: '12px 12px 0' };
const chip = (theme) => ({ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, padding: '3px 8px', borderRadius: 6, background: theme.colors.background, color: theme.colors.text, opacity: 0.75 });
const rejectReason = { margin: '10px 12px 0', fontSize: 12, color: '#f5222d' };
const pendingNotice = (theme, isProtected) => ({ margin: '10px 12px 0', padding: '8px 10px', borderRadius: 8, fontSize: 12, lineHeight: 1.4, fontWeight: isProtected ? 600 : 400, color: isProtected ? theme.colors.primary : theme.colors.text, background: isProtected ? `${theme.colors.primary}14` : 'rgba(250,173,20,0.12)', opacity: isProtected ? 1 : 0.9 });
